import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import cytoscape from "cytoscape";
import { api, getToken } from "../utils/helpers";
import i18n from "../i18n";

// Liest einen CSS-Token-Wert zur Laufzeit vom uebergebenen Element (Cytoscape rendert
// auf Canvas, wo var() nicht greift). WICHTIG: von einem Element INNERHALB .iio lesen,
// nicht von document.documentElement - dort sind die iio-Tokens (inkl. Dark-Override) nicht definiert.
const cssVar = (el, name, fallback = "") => {
  try { return getComputedStyle(el || document.documentElement).getPropertyValue(name).trim() || fallback; }
  catch { return fallback; }
};

function StatTile({ label, value }) {
  return (
    <div style={{ background: "var(--iio-bg-2)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-md)", padding: "var(--iio-s-4)", minWidth: 0 }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: "var(--iio-text-1)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--iio-text-3)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

// Kleine horizontale Balken-Tabelle fuer Rangliste-KPIs.
function BarRows({ rows, labelKey, valueKey, max }) {
  const top = max || Math.max(1, ...rows.map(r => r[valueKey] || 0));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 44px", alignItems: "center", gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "var(--iio-text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r[labelKey]}>{r[labelKey]}</div>
            <div style={{ height: 4, borderRadius: 2, background: "var(--iio-bg-3)", marginTop: 3 }}>
              <div style={{ height: 4, borderRadius: 2, width: `${Math.round(((r[valueKey] || 0) / top) * 100)}%`, background: "var(--iio-signal)" }} />
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: "var(--iio-text-2)", fontVariantNumeric: "tabular-nums" }}>{r[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    <section style={{ background: "var(--iio-bg-1)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-md)", padding: "var(--iio-s-5)" }}>
      <h2 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: "var(--iio-text-1)" }}>{title}</h2>
      {hint && <p style={{ fontSize: 11, color: "var(--iio-text-3)", margin: "2px 0 12px" }}>{hint}</p>}
      {children}
    </section>
  );
}

export default function AiInsights() {
  const { t } = useTranslation(["ki", "gemeinsam"]);
  const [kpis, setKpis] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [scope, setScope] = useState("");
  const [graph, setGraph] = useState({ nodes: [], edges: [], available: true });
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [error, setError] = useState(null);
  const [themeTick, setThemeTick] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [clearMsg, setClearMsg] = useState("");
  const [scopeToClear, setScopeToClear] = useState("");
  const [selectedNode, setSelectedNode] = useState(null); // {label,scope,facts:[{text,partner,out,veraltet}]}
  const containerRef = useRef(null);
  const cyRef = useRef(null);

  // Bei Theme-Wechsel (data-theme auf <html>) den Graphen mit neuen Farben neu aufbauen.
  useEffect(() => {
    const obs = new MutationObserver(() => setThemeTick(t => t + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const loadGraph = useCallback(async (sc) => {
    setGraphLoading(true);
    try {
      const q = sc ? `?scope=${encodeURIComponent(sc)}&limit=200` : "?limit=200";
      setGraph(await api(`/api/admin/memory/graph${q}`));
    } catch (e) { setError(e.message); } finally { setGraphLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [k, s] = await Promise.all([api("/api/admin/memory/kpis"), api("/api/admin/memory/scopes")]);
        setKpis(k); setScopes(s.scopes || []);
        await loadGraph("");
      } catch (e) { setError(e.message); } finally { setLoading(false); }
    })();
  }, [loadGraph]);

  // Cytoscape-Graph aufbauen/aktualisieren. themeTick -> Neu-Rendern bei Theme-Wechsel.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !graph.nodes) return;
    // Token-Werte vom Container lesen (liegt in .iio -> respektiert Light/Dark-Override).
    const nodeFill = cssVar(el, "--iio-brand-ink-3", "#135E70");
    const signal = cssVar(el, "--iio-signal", "#E85D2E");
    const border = cssVar(el, "--iio-border", "#c9d2dc");
    const text1 = cssVar(el, "--iio-text-1", "#1a2733");
    const text3 = cssVar(el, "--iio-text-3", "#7b8896");
    const bg1 = cssVar(el, "--iio-bg-1", "#ffffff");
    if (cyRef.current) { cyRef.current.destroy(); cyRef.current = null; }
    const cy = cytoscape({
      container: el,
      elements: [
        ...graph.nodes.map(n => ({ data: { id: n.id, label: n.label, scope: n.scope } })),
        ...graph.edges.map((e, i) => ({ data: { id: "e" + i, source: e.source, target: e.target, label: e.label, veraltet: e.veraltet } })),
      ],
      style: [
        { selector: "node", style: { "background-color": nodeFill, "border-width": 2, "border-color": bg1, label: "data(label)", "font-size": "9px", color: text1, "text-outline-color": bg1, "text-outline-width": 2, "text-wrap": "wrap", "text-max-width": "90px", "text-valign": "bottom", "text-margin-y": 3, width: 22, height: 22 } },
        { selector: "node:selected", style: { "background-color": signal, width: 28, height: 28 } },
        { selector: "edge", style: { width: 1.2, "line-color": border, "target-arrow-color": border, "target-arrow-shape": "triangle", "curve-style": "bezier", "arrow-scale": 0.7 } },
        { selector: "edge[?veraltet]", style: { "line-style": "dashed", opacity: 0.45 } },
        { selector: "edge:selected", style: { "line-color": signal, "target-arrow-color": signal, width: 2, label: "data(label)", "font-size": "8px", color: text3, "text-background-color": bg1, "text-background-opacity": 0.9, "text-background-padding": "2px" } },
      ],
      layout: { name: "cose", animate: false, idealEdgeLength: 95, nodeRepulsion: 9000, padding: 20 },
      wheelSensitivity: 0.25,
    });
    // Knoten-Klick -> Detail-Panel mit allen verknuepften Fakten (wie in Obsidian).
    setSelectedNode(null);
    cy.on("tap", "node", evt => {
      const d = evt.target.data();
      const facts = [];
      for (const ed of graph.edges) {
        if (ed.source !== d.id && ed.target !== d.id) continue;
        const partnerId = ed.source === d.id ? ed.target : ed.source;
        const pn = graph.nodes.find(nn => nn.id === partnerId);
        facts.push({ text: ed.label || "", partner: (pn && pn.label) || "?", out: ed.source === d.id, veraltet: !!ed.veraltet });
      }
      setSelectedNode({ label: d.label, scope: d.scope, facts });
    });
    cy.on("tap", evt => { if (evt.target === cy) setSelectedNode(null); });
    cyRef.current = cy;
    return () => { try { cy.destroy(); } catch {} cyRef.current = null; };
  }, [graph, themeTick]);

  const onScope = e => { const v = e.target.value; setScope(v); loadGraph(v); };

  const reloadAll = useCallback(async () => {
    try {
      const [k, s] = await Promise.all([api("/api/admin/memory/kpis"), api("/api/admin/memory/scopes")]);
      setKpis(k); setScopes(s.scopes || []); setScope(""); await loadGraph("");
    } catch (e) { setError(e.message); }
  }, [loadGraph]);

  const runClear = useCallback(async (path, body, label) => {
    setClearBusy(true); setClearMsg("");
    try {
      const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(body || {}) });
      const data = await res.json();
      const memoryErrors = { TRANSPORT_MEMORY_PENDING: "transportAusstehend",
        TRANSPORT_MEMORY_INVALID_SCOPE: "scopeUngueltig", TRANSPORT_MEMORY_INVALID_RANGE: "zeitraumUngueltig" };
      if (!res.ok || data.ok === false) throw new Error(memoryErrors[data.code]
        ? i18n.t(`ki:insights.verwalten.${memoryErrors[data.code]}`) : data.error || i18n.t("gemeinsam:zustand.fehler"));
      setClearMsg(i18n.t("ki:insights.verwalten.ergebnis", { was: label, count: data.deleted ?? 0 }));
      await reloadAll();
    } catch (e) { setClearMsg(i18n.t("ki:insights.verwalten.fehler", { meldung: e.message })); } finally { setClearBusy(false); setConfirmAll(false); }
  }, [reloadAll]);
  const memAvailable = kpis ? kpis.available : true;

  return (
    <div className="iio" style={{ padding: "var(--iio-s-6)", maxWidth: 1400, margin: "0 auto", display: "flex", flexDirection: "column", gap: "var(--iio-s-5)" }}>
      <header>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--iio-signal)", fontWeight: 600 }}>{t("insights.kopf.bereich")}</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "2px 0 0", color: "var(--iio-text-1)" }}>{t("insights.kopf.titel")}</h1>
        <p style={{ fontSize: 13, color: "var(--iio-text-2)", margin: "6px 0 0", maxWidth: 760 }}>
          {t("insights.kopf.beschreibung")}
        </p>
      </header>

      {error && <div style={{ background: "var(--iio-bg-2)", border: "1px solid var(--iio-critical)", borderRadius: "var(--iio-r-md)", padding: "var(--iio-s-4)", color: "var(--iio-critical)", fontSize: 13 }}>{t("insights.zustand.fehler", { meldung: error })}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--iio-text-3)" }}>{t("insights.zustand.laedt")}</div>
      ) : !memAvailable ? (
        <div style={{ background: "var(--iio-bg-1)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-md)", padding: "var(--iio-s-8)", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "var(--iio-text-1)", fontWeight: 600 }}>{t("insights.leer.titel")}</div>
          <div style={{ fontSize: 12, color: "var(--iio-text-3)", marginTop: 6 }}>{t("insights.leer.text")}</div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--iio-s-3)" }}>
            <StatTile label={t("insights.kennzahl.episoden")} value={kpis.totals.episoden} />
            <StatTile label={t("insights.kennzahl.entitaeten")} value={kpis.totals.entitaeten} />
            <StatTile label={t("insights.kennzahl.fakten")} value={kpis.totals.fakten} />
            <StatTile label={t("insights.kennzahl.scopes")} value={kpis.totals.scopes} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "var(--iio-s-4)", alignItems: "start" }}>
            <Panel title={t("insights.fix.titel")} hint={t("insights.fix.hinweis")}>
              {kpis.fixSchwerpunkte.length ? <BarRows rows={kpis.fixSchwerpunkte} labelKey="thema" valueKey="korrekturen" /> : <div style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("insights.fix.leer")}</div>}
            </Panel>

            <Panel title={t("insights.proScope.titel")} hint={t("insights.proScope.hinweis")}>
              {kpis.proScope.length ? <BarRows rows={kpis.proScope} labelKey="scope" valueKey="eintraege" /> : <div style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("insights.proScope.leer")}</div>}
            </Panel>

            <Panel title={t("insights.terminologie.titel")} hint={t("insights.terminologie.hinweis")}>
              {kpis.terminologie.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {kpis.terminologie.map((term, i) => (
                    <div key={i} style={{ fontSize: 12, color: "var(--iio-text-1)", display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${term.von} → ${term.nach}`}>{term.von} → {term.nach}</span>
                      <span style={{ fontWeight: 600, color: "var(--iio-text-3)" }}>{term.versionen}×</span>
                    </div>
                  ))}
                </div>
              ) : <div style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("insights.terminologie.leer")}</div>}
            </Panel>

            <Panel title={t("insights.lernkurve.titel")} hint={t("insights.lernkurve.hinweis")}>
              {kpis.lernkurve.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {kpis.lernkurve.map((m, i) => (
                    <div key={i} style={{ fontSize: 12, color: "var(--iio-text-2)", display: "flex", justifyContent: "space-between", fontVariantNumeric: "tabular-nums" }}>
                      <span>{m.monat}</span>
                      <span><strong style={{ color: "var(--iio-signal)" }}>{m.korrekturen}</strong> {t("insights.lernkurve.korrekturenKurz")} / {m.generierungen} {t("insights.lernkurve.generierungenKurz")}</span>
                    </div>
                  ))}
                </div>
              ) : <div style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("insights.lernkurve.leer")}</div>}
            </Panel>
          </div>

          <Panel title={t("insights.graph.titel")} hint={t("insights.graph.hinweis")}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, color: "var(--iio-text-2)" }}>{t("insights.graph.scope")}</label>
              <select value={scope} onChange={onScope} style={{ fontSize: 12, padding: "4px 8px", borderRadius: "var(--iio-r-sm)", border: "1px solid var(--iio-border)", background: "var(--iio-bg-2)", color: "var(--iio-text-1)" }}>
                <option value="">{t("insights.graph.alleScopes")}</option>
                {scopes.map(s => <option key={s.scope} value={s.scope}>{s.scope} ({s.count})</option>)}
              </select>
              <span style={{ fontSize: 11, color: "var(--iio-text-3)" }}>{graphLoading ? t("insights.graph.laedt") : `${t("insights.graph.knoten", { count: graph.nodes.length })}, ${t("insights.graph.kanten", { count: graph.edges.length })}`}</span>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
              <div ref={containerRef} style={{ flex: "1 1 480px", minWidth: 0, height: 460, background: "var(--iio-bg-0)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-sm)" }} />
              {selectedNode && (
                <div style={{ width: 300, flexShrink: 0, maxHeight: 460, overflowY: "auto", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-sm)", padding: "var(--iio-s-4)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--iio-text-1)", lineHeight: 1.3, minWidth: 0, overflowWrap: "anywhere" }}>{selectedNode.label}</div>
                    <button onClick={() => setSelectedNode(null)} title={t("gemeinsam:aktion.schliessen")} style={{ border: "1px solid var(--iio-border)", background: "transparent", color: "var(--iio-text-3)", borderRadius: "var(--iio-r-xs)", cursor: "pointer", fontSize: 11, padding: "1px 7px", flexShrink: 0 }}>×</button>
                  </div>
                  {selectedNode.scope && <div style={{ fontSize: 10.5, color: "var(--iio-text-3)", marginTop: 4, overflowWrap: "anywhere" }}>{t("insights.knoten.scope", { scope: selectedNode.scope })}</div>}
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--iio-text-3)", margin: "14px 0 8px" }}>{t("insights.knoten.fakten", { anzahl: selectedNode.facts.length })}</div>
                  {selectedNode.facts.length ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {selectedNode.facts.map((f, i) => (
                        <div key={i} style={{ borderLeft: `2px solid ${f.veraltet ? "var(--iio-border)" : "var(--iio-signal)"}`, paddingLeft: 8, opacity: f.veraltet ? 0.55 : 1 }}>
                          <div style={{ fontSize: 12, color: "var(--iio-text-1)", lineHeight: 1.45 }}>{f.text || t("insights.knoten.ohneBeschreibung")}</div>
                          <div style={{ fontSize: 10.5, color: "var(--iio-text-3)", marginTop: 2 }}>{f.out ? "→" : "←"} {f.partner}{f.veraltet ? t("insights.knoten.veraltet") : ""}</div>
                        </div>
                      ))}
                    </div>
                  ) : <div style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("insights.knoten.leer")}</div>}
                </div>
              )}
            </div>
            {!graphLoading && !graph.nodes.length && <div style={{ fontSize: 12, color: "var(--iio-text-3)", marginTop: 8 }}>{t("insights.graph.leer")}</div>}
          </Panel>

          <Panel title={t("insights.verwalten.titel")} hint={t("insights.verwalten.hinweis")}>
            {clearMsg && <div style={{ fontSize: 12, color: "var(--iio-text-2)", marginBottom: 12, background: "var(--iio-bg-2)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-sm)", padding: "8px 10px" }}>{clearMsg}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "var(--iio-s-4)", alignItems: "start" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-1)", marginBottom: 6 }}>{t("insights.verwalten.scopeTitel")}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <select value={scopeToClear} onChange={e => setScopeToClear(e.target.value)} style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "6px 8px", borderRadius: "var(--iio-r-sm)", border: "1px solid var(--iio-border)", background: "var(--iio-bg-2)", color: "var(--iio-text-1)" }}>
                    <option value="">{t("insights.verwalten.scopeWaehlen")}</option>
                    {scopes.map(s => <option key={s.scope} value={s.scope}>{s.scope} ({s.count})</option>)}
                  </select>
                  <button className="iio-btn iio-btn--ghost" disabled={clearBusy || !scopeToClear} onClick={() => runClear("/api/admin/memory/clear-scope", { scope: scopeToClear }, t("insights.verwalten.wasScope"))} style={{ fontSize: 12, padding: "6px 12px" }}>{t("gemeinsam:aktion.loeschen")}</button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-1)", marginBottom: 6 }}>{t("insights.verwalten.zeitraumTitel")}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", borderRadius: "var(--iio-r-sm)", border: "1px solid var(--iio-border)", background: "var(--iio-bg-2)", color: "var(--iio-text-1)" }} />
                  <span style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("insights.verwalten.bis")}</span>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", borderRadius: "var(--iio-r-sm)", border: "1px solid var(--iio-border)", background: "var(--iio-bg-2)", color: "var(--iio-text-1)" }} />
                  <button className="iio-btn iio-btn--ghost" disabled={clearBusy || !fromDate || !toDate} onClick={() => runClear("/api/admin/memory/clear-range", { from: fromDate, to: toDate }, t("insights.verwalten.wasZeitraum"))} style={{ fontSize: 12, padding: "6px 12px" }}>{t("gemeinsam:aktion.loeschen")}</button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-critical)", marginBottom: 6 }}>{t("insights.verwalten.allesTitel")}</div>
                {confirmAll ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--iio-text-2)" }}>{t("insights.verwalten.wirklich")}</span>
                    <button className="iio-btn iio-btn--ghost" disabled={clearBusy} onClick={() => runClear("/api/admin/memory/clear", {}, t("insights.verwalten.wasAlles"))} style={{ fontSize: 12, padding: "6px 12px", borderColor: "var(--iio-critical)", color: "var(--iio-critical)" }}>{t("insights.verwalten.jaAlles")}</button>
                    <button className="iio-btn iio-btn--ghost" disabled={clearBusy} onClick={() => setConfirmAll(false)} style={{ fontSize: 12, padding: "6px 12px" }}>{t("gemeinsam:aktion.abbrechen")}</button>
                  </div>
                ) : (
                  <button className="iio-btn iio-btn--ghost" disabled={clearBusy} onClick={() => setConfirmAll(true)} style={{ fontSize: 12, padding: "6px 12px", borderColor: "var(--iio-critical)", color: "var(--iio-critical)" }}>{t("insights.verwalten.allesKnopf")}</button>
                )}
              </div>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
